import { Coins } from 'lucide-react'
import type { Project } from '../data/projectTypes'
import { useGetProjectUsageQuery } from '../services/projectDataApi'
import { UsageTaskList, RefundedBadge } from './usageView'

// Credit usage for a single project, shown on the project settings page. Reuses the same usage
// components as the user settings Usage section. The /api/usage?project_id endpoint is gated to
// project members/admins, so non-members (e.g. public-project viewers) get a 403 — in that case the
// section hides itself rather than showing an error.
export default function ProjectUsageSection({ project }: { project: Project }) {
  const usageQuery = useGetProjectUsageQuery(project.id)
  if (usageQuery.isError) return null
  const usage = usageQuery.data?.project ?? null

  const total = usage?.total_credits ?? 0
  const taskCount = usage?.tasks?.length ?? 0
  const refunded = usage?.refunded_credits ?? 0
  const qaRefunded = usage?.qa_costs_refunded ?? false

  return (
    <section className="settings-section" data-testid="project-usage">
      <h3 className="settings-section__title">
        <Coins size={16} aria-hidden="true" /> Usage
        {qaRefunded && <RefundedBadge credits={refunded} />}
      </h3>
      <p className="settings-section__desc">
        Credit usage for this project — every metered task and the credits it consumed.
        {qaRefunded && ' This project’s QA costs have been refunded.'}
      </p>
      {usageQuery.isLoading ? (
        <div className="settings-usage__status">Loading...</div>
      ) : (
        <>
          <div className="settings-usage__summary">
            <div className="settings-usage__summary-item">
              <span className="settings-usage__summary-value">{total.toLocaleString()}</span>
              <span className="settings-usage__summary-label">Credits used</span>
            </div>
            <div className="settings-usage__summary-item" data-testid="project-usage-budget">
              <span className="settings-usage__summary-value">{project.budget == null ? 'Unlimited' : project.budget.toLocaleString()}</span>
              <span className="settings-usage__summary-label">Credit budget</span>
            </div>
            <div className="settings-usage__summary-item">
              <span className="settings-usage__summary-value">{taskCount.toLocaleString()}</span>
              <span className="settings-usage__summary-label">Metered tasks</span>
            </div>
          </div>
          {usage?.tasks && usage.tasks.length > 0 ? (
            <UsageTaskList tasks={usage.tasks} />
          ) : (
            <p className="settings-usage__empty" data-testid="project-usage-empty">No credits used yet.</p>
          )}
        </>
      )}
    </section>
  )
}
